export const OPEN_EMS_BASE =
  (typeof process !== 'undefined' &&
    process.env.REACT_APP_OPEN_EMS_UI_URL &&
    String(process.env.REACT_APP_OPEN_EMS_UI_URL).replace(/\/$/, '')) ||
  'https://220-km.com:9220';

/** Open EMS UI deep link for a Deye inverter device SN. */
export function buildOpenEmsInverterUrl(deviceSn) {
  const sn = String(deviceSn || '').trim();
  if (!sn) return null;
  return `${OPEN_EMS_BASE}/?market=oree&zone=ES&exportMetric=monthly_rates&inverter=deye%3A${encodeURIComponent(sn)}`;
}

/** Open EMS UI deep link for a GridLab BESS device id. */
export function buildOpenEmsGridlabUrl(deviceId) {
  const id = String(deviceId || '').trim();
  if (!id) return null;
  return `${OPEN_EMS_BASE}/?market=oree&zone=ES&exportMetric=monthly_rates&inverter=gridlab%3A${encodeURIComponent(id)}`;
}

/** Pick inverter URL for the BESS side label (single binding, capacity match, or first). */
export function resolveOpenEmsInverterUrl(bindings, totalCapacityKwh) {
  if (!bindings?.length) return null;
  if (bindings.length === 1) {
    return buildOpenEmsInverterUrl(bindings[0].deviceSn);
  }
  const total = Math.round(Number(totalCapacityKwh));
  if (Number.isFinite(total) && total > 0) {
    const matched = bindings.find(entry => Math.round(Number(entry.capacityKwh)) === total);
    if (matched) return buildOpenEmsInverterUrl(matched.deviceSn);
  }
  return buildOpenEmsInverterUrl(bindings[0].deviceSn);
}

export const TRACK_INVERTER_RE = /trackInverter\s*:\s*(\d+)(?:\s+bat(\d+)\s*kwh)?/gi;
export const TRACK_GRIDLAB_RE = /trackGridlab\s*:\s*(\d+)(?:\s+bat(\d+)\s*kwh)?/gi;
export const BAT_CAPACITY_KWH_RE = /bat\s*(\d+)\s*kwh/i;

/** True when station.name includes trackInverter markers (EMS carousel layout). */
export function stationNameHasTrackInverter(stationName) {
  return /trackInverter\s*:\s*\d+/i.test(String(stationName || ''));
}

/** True when station.name includes trackGridlab markers. */
export function stationNameHasTrackGridlab(stationName) {
  return /trackGridlab\s*:\s*\d+/i.test(String(stationName || ''));
}

/** True when station has any Open EMS BESS binding (Deye or GridLab). */
export function stationNameHasEmsBinding(stationName) {
  return stationNameHasTrackInverter(stationName) || stationNameHasTrackGridlab(stationName);
}
/** Insolation % at/above which the day counts as sunny (BESS boost window). */
export const OPEN_EMS_BATTERY_LIMIT_INSOLATION_PCT = 80;
/** Extra slider headroom during sunny midday (PV can refill BESS). */
export const OPEN_EMS_SUNNY_BESS_BOOST_MULTIPLIER = 1.5;
export const OPEN_EMS_SUNNY_BESS_BOOST_HOUR_START = 10;
export const OPEN_EMS_SUNNY_BESS_BOOST_HOUR_END = 13;
export const OPEN_EMS_PIN9220_LABEL_RE = /pin9220/i;

export function shortOpenEmsInverterLabel(label) {
  const text = String(label || '').trim();
  const dashIdx = text.indexOf('—');
  const shortText = dashIdx > 0 ? text.slice(0, dashIdx) : text;
  return shortText.trim();
}

export async function findOpenEmsPin9220Inverter() {
  try {
    const response = await fetch(`${OPEN_EMS_BASE}/api/deye/inverters`, { cache: 'no-store' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.configured) return null;
    const item = (data.items || []).find(row => OPEN_EMS_PIN9220_LABEL_RE.test(String(row?.label || '')));
    const deviceSn = String(item?.deviceSn || '').trim();
    if (!deviceSn) return null;
    return {
      deviceSn,
      label: shortOpenEmsInverterLabel(item.label),
      openEmsUrl: buildOpenEmsInverterUrl(deviceSn),
    };
  } catch {
    return null;
  }
}

export async function fetchOpenEmsEvPortsFleetPower() {
  try {
    const [dcResponse, acResponse] = await Promise.all([
      fetch(`${OPEN_EMS_BASE}/api/b2b/ev-ports-power?acdc=dc`, { cache: 'no-store' }),
      fetch(`${OPEN_EMS_BASE}/api/b2b/ev-ports-power?acdc=ac`, { cache: 'no-store' }),
    ]);
    const [dcData, acData] = await Promise.all([
      dcResponse.json().catch(() => ({})),
      acResponse.json().catch(() => ({})),
    ]);
    let powerW = 0;
    let activeSessions = 0;
    let ok = false;
    for (const data of [dcData, acData]) {
      if (data?.ok && data.powerW != null && Number.isFinite(Number(data.powerW))) {
        powerW += Math.max(0, Number(data.powerW));
        activeSessions += Number(data.activeSessions) || 0;
        ok = true;
      }
    }
    return ok ? { powerW, activeSessions } : null;
  } catch {
    return null;
  }
}

export async function fetchOpenEmsChargingTariffAvg(days = 7) {
  try {
    const response = await fetch(`${OPEN_EMS_BASE}/api/b2b/charging-network-tariff-avg?days=${days}`, {
      cache: 'no-store',
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.ok && Number.isFinite(Number(data.avgUahPerKwh))) {
      return Number(data.avgUahPerKwh);
    }
    return null;
  } catch {
    return null;
  }
}

export function buildOpenEmsEvFleetUrl() {
  return `${OPEN_EMS_BASE}/`;
}
export const LIMIT_EMS_SLIDER_MARKER = 'limitEmsSlider';

/** Backend appends ``ocppPortNumber:N`` directly to station.name (no separator). */
export function stripOcppPortNumberFromStationName(stationName) {
  return String(stationName || '').replace(/ocppPortNumber:(?:\d+|unknown)$/i, '');
}

/** True when station.name includes limitEmsSlider (cap charge slider by available BESS energy). */
export function isLimitEmsSliderEnabled(stationName) {
  const normalized = stripOcppPortNumberFromStationName(stationName);
  return new RegExp(`\\b${LIMIT_EMS_SLIDER_MARKER}\\b`, 'i').test(normalized);
}

export function parseBatCapacityKwhFromLabel(label) {
  const match = BAT_CAPACITY_KWH_RE.exec(String(label || ''));
  if (!match) return null;
  const kwh = Number(match[1]);
  return Number.isFinite(kwh) && kwh > 0 ? kwh : null;
}

/** Device SN + bat capacity from ``trackInverter:2512291445 bat15kwh`` in station.name. */
export function parseTrackInverterEntriesFromStationName(stationName) {
  const entries = [];
  const text = String(stationName || '');
  const re = new RegExp(TRACK_INVERTER_RE.source, 'gi');
  let match = re.exec(text);
  while (match) {
    const deviceSn = String(match[1] || '').trim();
    if (!deviceSn || entries.some(entry => entry.deviceSn === deviceSn)) {
      match = re.exec(text);
      continue;
    }
    const capacityRaw = match[2];
    const capacityKwh =
      capacityRaw != null && Number.isFinite(Number(capacityRaw)) && Number(capacityRaw) > 0
        ? Number(capacityRaw)
        : null;
    entries.push({ deviceSn, capacityKwh });
    match = re.exec(text);
  }
  return entries;
}

/** Device id + bat capacity from ``trackGridlab:16 bat1075kwh`` in station.name. */
export function parseTrackGridlabEntriesFromStationName(stationName) {
  const entries = [];
  const text = String(stationName || '');
  const re = new RegExp(TRACK_GRIDLAB_RE.source, 'gi');
  let match = re.exec(text);
  while (match) {
    const deviceId = String(match[1] || '').trim();
    if (!deviceId || entries.some(entry => entry.deviceId === deviceId)) {
      match = re.exec(text);
      continue;
    }
    const capacityRaw = match[2];
    const capacityKwh =
      capacityRaw != null && Number.isFinite(Number(capacityRaw)) && Number(capacityRaw) > 0
        ? Number(capacityRaw)
        : null;
    entries.push({ deviceId, capacityKwh });
    match = re.exec(text);
  }
  return entries;
}

/** True when station.name includes trackInverter with battery capacity (Open EMS storage). */
export function stationNameHasEnergyStorage(stationName) {
  return (
    parseTrackInverterEntriesFromStationName(stationName).some(
      entry => entry.capacityKwh != null && entry.capacityKwh > 0
    ) ||
    parseTrackGridlabEntriesFromStationName(stationName).some(
      entry => entry.capacityKwh != null && entry.capacityKwh > 0
    )
  );
}

/** Device SNs from trackInverter markers in station.name. */
export function parseTrackInverterIdsFromStationName(stationName) {
  return parseTrackInverterEntriesFromStationName(stationName).map(entry => entry.deviceSn);
}

/**
 * Shared BESS identity for sibling EV ports (same trackInverter / trackGridlab set).
 * Ports with this key should show the same live SOC tariff.
 */
export function stationBessIdentityKey(station) {
  const name = station?.name || '';
  const deye = parseTrackInverterIdsFromStationName(name).map(id => `deye:${id}`);
  const gridlab = parseTrackGridlabEntriesFromStationName(name).map(entry => `gridlab:${entry.deviceId}`);
  const parts = [...deye, ...gridlab].sort();
  if (parts.length) {
    return parts.join(',');
  }
  return null;
}

export function formatTrackInverterMarker({ deviceSn, capacityKwh }) {
  const id = String(deviceSn || '').trim();
  if (!id) return '';
  const capacity = Number(capacityKwh);
  if (Number.isFinite(capacity) && capacity > 0) {
    return `trackInverter:${id} bat${Math.round(capacity)}kwh`;
  }
  return `trackInverter:${id}`;
}

export function formatTrackGridlabMarker({ deviceId, capacityKwh }) {
  const id = String(deviceId || '').trim();
  if (!id) return '';
  const capacity = Number(capacityKwh);
  if (Number.isFinite(capacity) && capacity > 0) {
    return `trackGridlab:${id} bat${Math.round(capacity)}kwh`;
  }
  return `trackGridlab:${id}`;
}

export function stripTrackInverterMarkersFromStationName(stationName) {
  return String(stationName || '')
    .replace(/\s*trackInverter\s*:\s*\d+(?:\s+bat\d+\s*kwh)?\s*/gi, '\n')
    .replace(/\s*trackGridlab\s*:\s*\d+(?:\s+bat\d+\s*kwh)?\s*/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

export function applyTrackInverterMarkersToStationName(stationName, entries) {
  const base = stripTrackInverterMarkersFromStationName(stationName);
  const markers = (entries || []).map(entry => formatTrackInverterMarker(entry)).filter(Boolean);
  if (markers.length === 0) return base;
  return base ? `${base}\n${markers.join('\n')}` : markers.join('\n');
}

/** Parsed Open EMS inverter binding (deviceSn + bat capacity from station.name). */
export function resolveEvPortInverterBinding(item, capacityKwh) {
  const deviceSn = String(item?.deviceSn || '').trim();
  const capacity = Number(capacityKwh);
  if (!deviceSn || !Number.isFinite(capacity) || capacity <= 0) return null;
  return { deviceSn, label: item.label, capacityKwh: capacity };
}

/** Bind inverters listed in station.name via trackInverter markers. */
export function resolveTrackInverterBindings(items, stationName) {
  const bySn = new Map();
  for (const item of items || []) {
    const sn = String(item?.deviceSn || '').trim();
    if (sn) bySn.set(sn, item);
  }

  const bindings = [];
  for (const entry of parseTrackInverterEntriesFromStationName(stationName)) {
    const item = bySn.get(entry.deviceSn);
    if (!item) continue;
    const binding = resolveEvPortInverterBinding(item, entry.capacityKwh);
    if (binding) bindings.push(binding);
  }
  return bindings;
}

/** Inverters bound via trackInverter markers in station.name. */
export function resolveEvPortInverterBindings(items, stationName) {
  return resolveTrackInverterBindings(items, stationName);
}

export async function fetchOpenEmsDeyeInverters() {
  try {
    const response = await fetch(`${OPEN_EMS_BASE}/api/deye/inverters`, { cache: 'no-store' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.configured) return [];
    return data.items || [];
  } catch {
    return [];
  }
}

/** Cluster key from Open EMS inverter label (site name before em dash). */
export function clusterKeyFromInverterLabel(label, deviceSn) {
  return shortOpenEmsInverterLabel(label) || String(deviceSn || '').trim();
}

/** Group Deye inverters that share the same site label (Open EMS cluster). */
export function buildDeyeInverterClusters(items) {
  const byKey = new Map();
  for (const row of items || []) {
    const deviceSn = String(row?.deviceSn || '').trim();
    if (!deviceSn) continue;
    const key = clusterKeyFromInverterLabel(row?.label, deviceSn);
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { key, representativeSn: deviceSn, clusterSns: [deviceSn] });
      continue;
    }
    prev.clusterSns.push(deviceSn);
    if (deviceSn < prev.representativeSn) prev.representativeSn = deviceSn;
  }
  return [...byKey.values()];
}

/** All device SNs on the same Deye site cluster as ``deviceSn``. */
export function resolveDeyeClusterSns(deviceSn, items) {
  const sn = String(deviceSn || '').trim();
  if (!sn) return [];
  for (const cluster of buildDeyeInverterClusters(items)) {
    if (cluster.clusterSns.includes(sn)) return cluster.clusterSns;
  }
  return [sn];
}

export async function fetchOpenEmsEssPowerRow(deviceSn) {
  const query = new URLSearchParams({ deviceSn: String(deviceSn || '').trim() });
  try {
    const response = await fetch(`${OPEN_EMS_BASE}/api/deye/ess-power?${query}`, { cache: 'no-store' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.ok) return null;
    return data;
  } catch {
    return null;
  }
}

/** Sum ESS power across a Deye cluster (matches Open EMS power-flow aggregation). */
export function aggregateDeyeEssPowerRows(rows) {
  const okRows = (rows || []).filter(row => row?.ok && row?.configured !== false);
  const uniqRows = [];
  const seenStationFallback = new Set();
  const seenTuple = new Set();

  for (const row of okRows) {
    const stId = row?.stationId == null ? '' : String(row.stationId).trim();
    if (row?.stationFallback === true && stId) {
      if (seenStationFallback.has(stId)) continue;
      seenStationFallback.add(stId);
      uniqRows.push(row);
      continue;
    }
    const tupleKey = [
      row?.batteryPowerW ?? 'n',
      row?.loadPowerW ?? 'n',
      row?.pvPowerW ?? 'n',
      row?.gridPowerW ?? 'n',
      row?.gridFrequencyHz ?? 'n',
    ].join('|');
    if (seenTuple.has(tupleKey)) continue;
    seenTuple.add(tupleKey);
    uniqRows.push(row);
  }

  const sumField = (key, floorZero = false) => {
    let has = false;
    let sum = 0;
    for (const row of uniqRows) {
      const value = row?.[key];
      if (value != null && Number.isFinite(Number(value))) {
        has = true;
        sum += Number(value);
      }
    }
    if (!has) return null;
    return floorZero ? Math.max(0, sum) : sum;
  };

  const batteryPowerW = sumField('batteryPowerW', false);
  return {
    pvPowerW: sumField('pvPowerW', true),
    batteryPowerW,
    batteryDischargePowerW: batteryPowerW != null && batteryPowerW > 0 ? batteryPowerW : null,
    gridPowerW: sumField('gridPowerW', false),
    loadPowerW: sumField('loadPowerW', true),
    socPercent: pickEssPowerSocPercent(uniqRows),
  };
}

/** Arithmetic mean of finite 0–100 SoC readings. Ignores missing inverters. */
export function averageFiniteSocPercents(values) {
  const nums = [];
  for (const value of values || []) {
    const n = Number(value);
    if (value != null && Number.isFinite(n) && n >= 0 && n <= 100) nums.push(n);
  }
  if (!nums.length) return null;
  return nums.reduce((sum, n) => sum + n, 0) / nums.length;
}

/**
 * Current SoC for a Deye site: mean of per-inverter readings.
 * ess-power SoC is only a fallback when every inverter reading is missing.
 */
export function clusterDisplaySocPercent(clusterSns, socBySn, essSoc) {
  const avg = averageFiniteSocPercents((clusterSns || []).map(sn => socBySn?.[sn]));
  if (avg != null) return avg;
  if (essSoc != null && Number.isFinite(Number(essSoc))) return Number(essSoc);
  return null;
}

/**
 * Plant SoC from /api/deye/ess-power rows. Do not sum — the same value is repeated per serial.
 */
export function pickEssPowerSocPercent(rows) {
  const socs = [];
  for (const row of rows || []) {
    const value = row?.socPercent;
    if (value != null && Number.isFinite(Number(value))) socs.push(Number(value));
  }
  if (socs.length === 0) return null;
  const first = socs[0];
  if (socs.every(value => Math.abs(value - first) < 0.05)) return first;
  return socs.reduce((sum, value) => sum + value, 0) / socs.length;
}

/** Prefer cluster-mean SoC from ess-power; fall back to per-inverter GET /soc. */
export function resolveDeyeDisplaySocPercent(essData, socData) {
  const essSoc = essData?.socPercent;
  if (essData?.ok && essSoc != null && Number.isFinite(Number(essSoc))) {
    return Math.round(Number(essSoc));
  }
  const deviceSoc = socData?.socPercent;
  if (socData?.ok === true && deviceSoc != null && Number.isFinite(Number(deviceSoc))) {
    return Math.round(Number(deviceSoc));
  }
  return null;
}

export async function fetchOpenEmsInverterSocMap(deviceSns) {
  const sns = [...new Set((deviceSns || []).map(sn => String(sn || '').trim()).filter(Boolean))];
  if (!sns.length) return {};
  try {
    const response = await fetch(`${OPEN_EMS_BASE}/api/deye/inverter-socs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceSns: sns }),
      cache: 'no-store',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok !== true || !Array.isArray(data.items)) return {};
    const map = {};
    for (const item of data.items) {
      const sn = item?.deviceSn != null ? String(item.deviceSn).trim() : '';
      if (!sn) continue;
      const soc = item.socPercent;
      map[sn] = soc != null && Number.isFinite(Number(soc)) ? Number(soc) : null;
    }
    return map;
  } catch {
    return {};
  }
}

export async function fetchOpenEmsClusterPowerTelemetry(deviceSn, inverters) {
  const clusterSns = resolveDeyeClusterSns(deviceSn, inverters);
  const [rows, socBySn] = await Promise.all([
    Promise.all(clusterSns.map(sn => fetchOpenEmsEssPowerRow(sn))),
    fetchOpenEmsInverterSocMap(clusterSns),
  ]);
  const aggregated = aggregateDeyeEssPowerRows(rows.filter(Boolean));
  const socPercent = clusterDisplaySocPercent(clusterSns, socBySn, aggregated.socPercent);
  if (socPercent != null) aggregated.socPercent = socPercent;
  return aggregated;
}

export async function fetchOpenEmsTelemetry(deviceSn) {
  const query = new URLSearchParams({ deviceSn });
  try {
    const [essResponse, socResponse, insolationResponse] = await Promise.all([
      fetch(`${OPEN_EMS_BASE}/api/deye/ess-power?${query}`, { cache: 'no-store' }),
      fetch(`${OPEN_EMS_BASE}/api/deye/soc?${query}`, { cache: 'no-store' }),
      fetch(`${OPEN_EMS_BASE}/api/deye/solar-insolation?${query}`, { cache: 'no-store' }),
    ]);
    const [essData, socData, insolationData] = await Promise.all([
      essResponse.json().catch(() => ({})),
      socResponse.json().catch(() => ({})),
      insolationResponse.json().catch(() => ({})),
    ]);

    const socPercent = resolveDeyeDisplaySocPercent(essResponse.ok ? essData : null, socResponse.ok ? socData : null);

    let pvPowerW = null;
    let batteryPowerW = null;
    let gridPowerW = null;
    let loadPowerW = null;
    if (essResponse.ok && essData.ok) {
      pvPowerW = essData.pvPowerW ?? null;
      batteryPowerW = essData.batteryPowerW ?? null;
      gridPowerW = essData.gridPowerW ?? null;
      loadPowerW = essData.loadPowerW ?? null;
    }

    let insolationTodayPct = null;
    let insolationTomorrowPct = null;
    if (insolationResponse.ok && insolationData.ok === true) {
      const todayPct = insolationData.today?.insolationPct;
      if (todayPct != null && Number.isFinite(Number(todayPct))) {
        insolationTodayPct = Math.round(Number(todayPct));
      }
      const tomorrowPct = insolationData.tomorrow?.insolationPct;
      if (tomorrowPct != null && Number.isFinite(Number(tomorrowPct))) {
        insolationTomorrowPct = Math.round(Number(tomorrowPct));
      }
    }

    return {
      socPercent,
      pvPowerW,
      batteryPowerW,
      batteryDischargePowerW: batteryPowerW != null && Number(batteryPowerW) > 0 ? Number(batteryPowerW) : null,
      gridPowerW,
      loadPowerW,
      insolationTodayPct,
      insolationTomorrowPct,
    };
  } catch {
    return {
      socPercent: null,
      pvPowerW: null,
      batteryPowerW: null,
      batteryDischargePowerW: null,
      gridPowerW: null,
      loadPowerW: null,
      insolationTodayPct: null,
      insolationTomorrowPct: null,
    };
  }
}

/** Map GridLab /power-flow JSON into the same telemetry shape as fetchOpenEmsTelemetry. */
export function mapGridlabPowerFlowToTelemetry(data) {
  if (!data || data.ok !== true) {
    return {
      socPercent: null,
      pvPowerW: null,
      batteryPowerW: null,
      batteryDischargePowerW: null,
      gridPowerW: null,
      loadPowerW: null,
      insolationTodayPct: null,
      insolationTomorrowPct: null,
    };
  }
  const socPercent =
    data.socPercent != null && Number.isFinite(Number(data.socPercent)) ? Math.round(Number(data.socPercent)) : null;
  const batteryPowerW =
    data.batteryPowerW != null && Number.isFinite(Number(data.batteryPowerW)) ? Number(data.batteryPowerW) : null;
  const pvPowerW = data.pvPowerW != null && Number.isFinite(Number(data.pvPowerW)) ? Number(data.pvPowerW) : null;
  const gridPowerW =
    data.gridPowerW != null && Number.isFinite(Number(data.gridPowerW)) ? Number(data.gridPowerW) : null;
  const loadPowerW =
    data.loadPowerW != null && Number.isFinite(Number(data.loadPowerW)) ? Number(data.loadPowerW) : null;
  return {
    socPercent,
    pvPowerW,
    batteryPowerW,
    batteryDischargePowerW: batteryPowerW != null && batteryPowerW > 0 ? batteryPowerW : null,
    gridPowerW,
    loadPowerW,
    insolationTodayPct: null,
    insolationTomorrowPct: null,
  };
}

export async function fetchGridlabPowerFlow(deviceId) {
  const id = String(deviceId || '').trim();
  if (!id) return mapGridlabPowerFlowToTelemetry(null);
  try {
    const query = new URLSearchParams({ deviceId: id });
    const response = await fetch(`${OPEN_EMS_BASE}/api/gridlab/power-flow?${query}`, {
      cache: 'no-store',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) return mapGridlabPowerFlowToTelemetry(null);
    return mapGridlabPowerFlowToTelemetry(data);
  } catch {
    return mapGridlabPowerFlowToTelemetry(null);
  }
}

export async function fetchGridlabInsolation(latitude, longitude) {
  const lat = Number(latitude);
  const lon = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return { insolationTodayPct: null, insolationTomorrowPct: null };
  }
  try {
    const query = new URLSearchParams({ lat: String(lat), lon: String(lon) });
    const response = await fetch(`${OPEN_EMS_BASE}/api/gridlab/solar-insolation?${query}`, {
      cache: 'no-store',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok !== true) {
      return { insolationTodayPct: null, insolationTomorrowPct: null };
    }
    let insolationTodayPct = null;
    let insolationTomorrowPct = null;
    const todayPct = data.today?.insolationPct;
    if (todayPct != null && Number.isFinite(Number(todayPct))) {
      insolationTodayPct = Math.round(Number(todayPct));
    }
    const tomorrowPct = data.tomorrow?.insolationPct;
    if (tomorrowPct != null && Number.isFinite(Number(tomorrowPct))) {
      insolationTomorrowPct = Math.round(Number(tomorrowPct));
    }
    return { insolationTodayPct, insolationTomorrowPct };
  } catch {
    return { insolationTodayPct: null, insolationTomorrowPct: null };
  }
}

/** Aggregate GridLab entries with optional Deye telemetry entries into display metrics. */
export async function fetchAggregatedGridlabTelemetry(entries, { latitude, longitude } = {}) {
  const telemetries = await Promise.all(
    (entries || []).map(async entry => {
      const telemetry = await fetchGridlabPowerFlow(entry.deviceId);
      return { capacityKwh: entry.capacityKwh, telemetry };
    })
  );
  const aggregated = aggregateOpenEmsTelemetry(telemetries);
  const insolation = await fetchGridlabInsolation(latitude, longitude);
  return {
    ...aggregated,
    insolationTodayPct: insolation.insolationTodayPct,
    insolationTomorrowPct: insolation.insolationTomorrowPct,
  };
}

/** Sum capacity-weighted SoC/available energy and power across multiple inverters on the same track. */
export function aggregateOpenEmsTelemetry(entries) {
  let totalCapacityKwh = 0;
  let weightedSocSum = 0;
  let socWeight = 0;
  let maxSelectableKwh = 0;
  let hasSelectable = false;
  let storedBatKwh = 0;
  let hasStored = false;
  let pvPowerW = null;
  let batteryPowerW = null;
  let batteryDischargePowerW = null;
  let gridPowerW = null;
  let loadPowerW = null;
  let insolationTodayPct = null;
  let insolationTomorrowPct = null;
  let insolationTodaySum = 0;
  let insolationTodayCount = 0;
  let insolationTomorrowSum = 0;
  let insolationTomorrowCount = 0;

  for (const entry of entries || []) {
    const capacityKwh = entry?.capacityKwh;
    if (capacityKwh == null) continue;

    totalCapacityKwh += capacityKwh;
    const telemetry = entry?.telemetry ?? entry;

    if (telemetry?.socPercent != null) {
      weightedSocSum += capacityKwh * telemetry.socPercent;
      socWeight += capacityKwh;
      // Cap EV slider by energy available to discharge from BESS, not empty charge room.
      const available = computeAvailableBatKwh(capacityKwh, telemetry.socPercent);
      if (available != null) {
        maxSelectableKwh += available;
        hasSelectable = true;
      }
      const stored = computeStoredBatKwh(capacityKwh, telemetry.socPercent);
      if (stored != null) {
        storedBatKwh += stored;
        hasStored = true;
      }
    }

    if (telemetry?.pvPowerW != null) {
      pvPowerW = (pvPowerW ?? 0) + Number(telemetry.pvPowerW);
    }
    if (telemetry?.batteryPowerW != null) {
      const bat = Number(telemetry.batteryPowerW);
      batteryPowerW = (batteryPowerW ?? 0) + bat;
      if (bat > 0) {
        batteryDischargePowerW = (batteryDischargePowerW ?? 0) + bat;
      }
    }
    if (telemetry?.batteryDischargePowerW != null && telemetry?.batteryPowerW == null) {
      const bat = Number(telemetry.batteryDischargePowerW);
      if (bat > 0) {
        batteryDischargePowerW = (batteryDischargePowerW ?? 0) + bat;
      }
    }
    if (telemetry?.gridPowerW != null) {
      const grid = Number(telemetry.gridPowerW);
      if (grid > 0) {
        gridPowerW = (gridPowerW ?? 0) + grid;
      }
    }
    if (telemetry?.loadPowerW != null) {
      loadPowerW = (loadPowerW ?? 0) + Number(telemetry.loadPowerW);
    }
    if (telemetry?.insolationTodayPct != null) {
      insolationTodaySum += telemetry.insolationTodayPct;
      insolationTodayCount += 1;
    }
    if (telemetry?.insolationTomorrowPct != null) {
      insolationTomorrowSum += telemetry.insolationTomorrowPct;
      insolationTomorrowCount += 1;
    }
  }

  if (insolationTodayCount > 0) {
    insolationTodayPct = Math.round(insolationTodaySum / insolationTodayCount);
  }
  if (insolationTomorrowCount > 0) {
    insolationTomorrowPct = Math.round(insolationTomorrowSum / insolationTomorrowCount);
  }

  return {
    batteryCapacityKwh: totalCapacityKwh > 0 ? totalCapacityKwh : null,
    socPercent: socWeight > 0 ? Math.round(weightedSocSum / socWeight) : null,
    maxSelectableKwh: hasSelectable ? maxSelectableKwh : null,
    storedBatKwh: hasStored ? storedBatKwh : null,
    pvPowerW,
    batteryPowerW,
    batteryDischargePowerW,
    gridPowerW,
    loadPowerW,
    insolationTodayPct,
    insolationTomorrowPct,
  };
}

function readEmsPowerKw({ pvPowerW, batteryPowerW, batteryDischargePowerW, gridPowerW, loadPowerW }) {
  const pvKw = pvPowerW != null ? Math.max(0, Number(pvPowerW)) / 1000 : 0;
  const batDischargeKw =
    batteryDischargePowerW != null && Number(batteryDischargePowerW) > 0
      ? Number(batteryDischargePowerW) / 1000
      : batteryPowerW != null && Number(batteryPowerW) > 0
        ? Number(batteryPowerW) / 1000
        : 0;
  const gridImportKw = gridPowerW != null && Number(gridPowerW) > 0 ? Number(gridPowerW) / 1000 : 0;
  const loadKw = loadPowerW != null && Number(loadPowerW) > 0 ? Number(loadPowerW) / 1000 : null;
  const renewableKw = pvKw + batDischargeKw;
  return { batDischargeKw, gridImportKw, loadKw, renewableKw };
}

function isBessSiteFullyGreen({ batDischargeKw, gridImportKw, loadKw, renewableKw }) {
  if (loadKw == null || batDischargeKw <= 0.02) {
    return false;
  }
  if (renewableKw >= loadKw * 0.9 && gridImportKw <= loadKw * 0.1) {
    return true;
  }
  // BESS covers most on-site load; grid import is a small share of total load.
  if (renewableKw >= loadKw * 0.85 && gridImportKw <= loadKw * 0.12) {
    return true;
  }
  return false;
}

/** Total green-energy share of on-site supply mix (0–100). */
export function computeSiteGreenEnergyPct({ pvPowerW, batteryPowerW, batteryDischargePowerW, gridPowerW, loadPowerW }) {
  const { batDischargeKw, gridImportKw, loadKw, renewableKw } = readEmsPowerKw({
    pvPowerW,
    batteryPowerW,
    batteryDischargePowerW,
    gridPowerW,
    loadPowerW,
  });

  if (renewableKw <= 0 && gridImportKw <= 0) {
    return null;
  }

  if (isBessSiteFullyGreen({ batDischargeKw, gridImportKw, loadKw, renewableKw })) {
    return 100;
  }

  const totalSupplyKw = renewableKw + gridImportKw;
  if (totalSupplyKw <= 0.001) {
    return null;
  }

  return Math.round(Math.min(100, (renewableKw / totalSupplyKw) * 100));
}

/** Green-energy share for EMS-backed EV charging (0–100). */
export function computeEmsRenewablePct({
  chargeKw,
  pvPowerW,
  batteryPowerW,
  batteryDischargePowerW,
  gridPowerW,
  loadPowerW,
  online = true,
}) {
  if (!online || chargeKw == null || !Number.isFinite(Number(chargeKw)) || Number(chargeKw) <= 0) {
    return null;
  }

  const charge = Number(chargeKw);
  const { batDischargeKw, gridImportKw, loadKw, renewableKw } = readEmsPowerKw({
    pvPowerW,
    batteryPowerW,
    batteryDischargePowerW,
    gridPowerW,
    loadPowerW,
  });

  if (renewableKw <= 0 && gridImportKw <= 0) {
    return 0;
  }

  if (isBessSiteFullyGreen({ batDischargeKw, gridImportKw, loadKw, renewableKw })) {
    return 100;
  }

  return Math.round(Math.min(100, (Math.min(charge, renewableKw) / charge) * 100));
}

/** Fleet-wide green-energy % from all configured Open EMS inverters. */
export async function fetchOpenEmsFleetGreenEnergyPct() {
  try {
    const response = await fetch(`${OPEN_EMS_BASE}/api/deye/inverters`, { cache: 'no-store' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.configured || !Array.isArray(data.items) || data.items.length === 0) {
      return null;
    }

    const telemetries = await Promise.all(
      data.items
        .map(item => String(item?.deviceSn || '').trim())
        .filter(Boolean)
        .map(deviceSn => fetchOpenEmsTelemetry(deviceSn))
    );

    const aggregated = aggregateOpenEmsTelemetry(telemetries.map(telemetry => ({ capacityKwh: 1, telemetry })));

    return computeSiteGreenEnergyPct(aggregated);
  } catch {
    return null;
  }
}

export function isKm220StationCharging(station) {
  return station?.job?.state === 'IN_PROGRESS';
}

/** Unique trackInverter device SNs from in-progress stations. */
export function collectTrackInverterSnsFromActiveStations(stations) {
  const sns = new Set();
  for (const station of stations || []) {
    if (!isKm220StationCharging(station)) continue;
    for (const deviceSn of parseTrackInverterIdsFromStationName(station?.name)) {
      sns.add(deviceSn);
    }
  }
  return [...sns];
}

/** Sum PV + battery discharge (W) from trackInverter-bound inverters on in-progress EMS ports. */
export function sumGreenPowerWForActiveEmsInverters(stations, telemetryBySn) {
  let greenW = 0;
  for (const deviceSn of collectTrackInverterSnsFromActiveStations(stations)) {
    const telemetry = telemetryBySn?.get?.(deviceSn) ?? telemetryBySn?.[deviceSn];
    if (!telemetry) continue;
    const pvPowerW = telemetry.pvPowerW;
    if (pvPowerW != null && Number(pvPowerW) > 0) {
      greenW += Number(pvPowerW);
    }
    const batteryPowerW = telemetry.batteryPowerW;
    if (batteryPowerW != null && Number(batteryPowerW) > 0) {
      greenW += Number(batteryPowerW);
    }
  }
  return greenW;
}

export async function sumGreenPowerWForActiveEmsClusters(stations, inverters) {
  const clustersSeen = new Set();
  let greenW = 0;
  for (const deviceSn of collectTrackInverterSnsFromActiveStations(stations)) {
    const clusterSns = resolveDeyeClusterSns(deviceSn, inverters);
    const clusterKey = clusterSns.slice().sort().join(',');
    if (clustersSeen.has(clusterKey)) continue;
    clustersSeen.add(clusterKey);
    const power = await fetchOpenEmsClusterPowerTelemetry(deviceSn, inverters);
    if (power.pvPowerW != null && Number(power.pvPowerW) > 0) {
      greenW += Number(power.pvPowerW);
    }
    if (power.batteryPowerW != null && Number(power.batteryPowerW) > 0) {
      greenW += Number(power.batteryPowerW);
    }
  }
  return greenW;
}

/** Green share of total fleet charging power (0–100): (PV + battery discharge) / total. */
export function computeGreenShareOfTotalPowerPct(totalPowerW, greenPowerW) {
  const total = Number(totalPowerW);
  const green = Math.max(0, Number(greenPowerW) || 0);
  if (!Number.isFinite(total) || total <= 0) return null;
  return Math.round(Math.min(100, (green / total) * 100));
}

/** @deprecated Use sumGreenPowerWForActiveEmsInverters */
export const sumSolarPowerWForActiveEmsInverters = sumGreenPowerWForActiveEmsInverters;

/** @deprecated Use computeGreenShareOfTotalPowerPct */
export const computeSolarShareOfTotalPowerPct = computeGreenShareOfTotalPowerPct;

export async function fetchKm220StationsAll(apiOrigin) {
  const origin = String(apiOrigin || '').replace(/\/$/, '');
  if (!origin) return [];
  try {
    const response = await fetch(`${origin}/api/device/v2/station/all?acdc=all`, { cache: 'no-store' });
    if (!response.ok) return [];
    const data = await response.json().catch(() => null);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/** Green % = sum(PV + battery discharge from active trackInverter ports) / total fleet power. */
export async function fetchOpenEmsActivePortsGreenEnergyPct(apiOrigin, totalPowerW) {
  try {
    const [stations, inverters] = await Promise.all([fetchKm220StationsAll(apiOrigin), fetchOpenEmsDeyeInverters()]);
    if (!collectTrackInverterSnsFromActiveStations(stations).length) return null;

    const greenPowerW = await sumGreenPowerWForActiveEmsClusters(stations, inverters);
    return computeGreenShareOfTotalPowerPct(totalPowerW, greenPowerW);
  } catch {
    return null;
  }
}

export async function fetchAggregatedOpenEmsTelemetry(bindings) {
  const inverters = await fetchOpenEmsDeyeInverters();
  const telemetries = await Promise.all(
    (bindings || []).map(async binding => {
      const [baseTelemetry, clusterPower] = await Promise.all([
        fetchOpenEmsTelemetry(binding.deviceSn),
        fetchOpenEmsClusterPowerTelemetry(binding.deviceSn, inverters),
      ]);
      return {
        capacityKwh: binding.capacityKwh,
        telemetry: {
          ...baseTelemetry,
          pvPowerW: clusterPower.pvPowerW ?? baseTelemetry.pvPowerW,
          batteryPowerW: clusterPower.batteryPowerW ?? baseTelemetry.batteryPowerW,
          batteryDischargePowerW: clusterPower.batteryDischargePowerW ?? baseTelemetry.batteryDischargePowerW,
          gridPowerW: clusterPower.gridPowerW ?? baseTelemetry.gridPowerW,
          loadPowerW: clusterPower.loadPowerW ?? baseTelemetry.loadPowerW,
          socPercent:
            clusterPower.socPercent != null && Number.isFinite(Number(clusterPower.socPercent))
              ? Math.round(Number(clusterPower.socPercent))
              : baseTelemetry.socPercent,
        },
      };
    })
  );
  return aggregateOpenEmsTelemetry(telemetries);
}

/**
 * When limitEmsSlider is enabled, always cap the EV charge slider by available BESS energy.
 * Weather/time only affect the sunny midday boost (see getOpenEmsSunnyBessBoostMultiplier).
 */
export function shouldApplyOpenEmsBatteryLimit() {
  return true;
}

/**
 * Midday sunny boost: 10:00–13:00 local time with insolation ≥ 80% → multiply BESS cap by 1.5.
 * Outside that window the multiplier is 1.
 */
export function getOpenEmsSunnyBessBoostMultiplier({ insolationTodayPct, now = new Date() } = {}) {
  const hour = now instanceof Date ? now.getHours() : new Date(now).getHours();
  if (
    !Number.isFinite(hour) ||
    hour < OPEN_EMS_SUNNY_BESS_BOOST_HOUR_START ||
    hour >= OPEN_EMS_SUNNY_BESS_BOOST_HOUR_END
  ) {
    return 1;
  }
  if (insolationTodayPct == null || Number(insolationTodayPct) < OPEN_EMS_BATTERY_LIMIT_INSOLATION_PCT) {
    return 1;
  }
  return OPEN_EMS_SUNNY_BESS_BOOST_MULTIPLIER;
}

/** Apply sunny midday BESS boost to an available-kWh slider cap. */
export function applyOpenEmsSunnyBessBoost(maxSelectableKwh, options = {}) {
  if (maxSelectableKwh == null) return null;
  const value = Number(maxSelectableKwh);
  if (!Number.isFinite(value) || value < 0) return maxSelectableKwh;
  const multiplier = getOpenEmsSunnyBessBoostMultiplier(options);
  return Math.floor(value * multiplier);
}

/** Remaining empty charge room (kWh) from total capacity and SoC 0..100 %. */
export function computeRemainingBatKwh(capacityKwh, socPercent) {
  if (capacityKwh == null || socPercent == null) return null;
  const capacity = Number(capacityKwh);
  const soc = Number(socPercent);
  if (!Number.isFinite(capacity) || capacity <= 0 || !Number.isFinite(soc)) return null;
  const clampedSoc = Math.min(100, Math.max(0, soc));
  const remaining = (capacity * (100 - clampedSoc)) / 100;
  return Math.max(0, Math.floor(remaining));
}

/**
 * Energy available to discharge from BESS (kWh) for EV slider caps.
 * Returns 0 when empty so the slider can be fully blocked.
 */
export function computeAvailableBatKwh(capacityKwh, socPercent) {
  if (capacityKwh == null || socPercent == null) return null;
  const capacity = Number(capacityKwh);
  const soc = Number(socPercent);
  if (!Number.isFinite(capacity) || capacity <= 0 || !Number.isFinite(soc)) return null;
  const clampedSoc = Math.min(100, Math.max(0, soc));
  return Math.max(0, Math.floor((capacity * clampedSoc) / 100));
}

/** Stored energy (kWh) in BESS from capacity and SoC; never returns 0. */
export function computeStoredBatKwh(capacityKwh, socPercent) {
  if (capacityKwh == null || socPercent == null) return null;
  const capacity = Number(capacityKwh);
  const soc = Number(socPercent);
  if (!Number.isFinite(capacity) || capacity <= 0 || !Number.isFinite(soc)) return null;
  const clampedSoc = Math.min(100, Math.max(0, soc));
  const stored = Math.floor((capacity * clampedSoc) / 100);
  return stored > 0 ? stored : null;
}
